import React, { useState, useEffect, useRef } from "react";
import { Modal } from "../CommonUIComponents/Modal/Modal";
import { Button } from "../CommonUIComponents/Button/Button";
import { EditableDelegateField } from "../CommonUIComponents/EditableDelegateField/EditableDelegateField";
import {
  MotifDatePicker,
  MotifDropdown,
  MotifDropdownItem,
  MotifFormField,
} from "@ey-xd/motif-react";
import { utcToLocalDateInput } from "../../util/dateUtil";
import "./AOAEditServiceDetailsModal.scss";

const MAX_DELEGATES = 5;

const delegatesKey = (list) =>
  (list || [])
    .map(
      (d) =>
        `${d.emailAddress || d.delegateEmail || ""}|${d.firstName || ""}|${d.lastName || ""}`,
    )
    .sort()
    .join(",");

const NATURE_OF_ENGAGEMENT_OPTIONS = [
  "Audit of financial statements",
  "Interim review",
];

// Normalize any incoming date value (UTC ISO date-time, yyyy-mm-dd or
// dd/mm/yyyy) into a local-calendar yyyy-mm-dd string for the date picker.
// UTC date-times are shifted into the user's local timezone.
function formatToISO(v) {
  return utcToLocalDateInput(v);
}

function normalizeDateValue(val) {
  const toLocal = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  if (Array.isArray(val)) {
    const first = val[0];
    return first ? toLocal(new Date(first)) : "";
  }
  if (val instanceof Date) return toLocal(val);
  if (typeof val === "string") {
    const match = val.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : val.slice(0, 10);
  }
  return "";
}

export function AOAEditServiceDetailsModal({
  show,
  onClose,
  onSave,
  data = {},
  currentStageId,
  loading = false,
  error = "",
  onClearError,
  isCurrentUserDelegate = false,
}) {
  const [natureOfEngagement, setNatureOfEngagement] = useState("");
  const [accessReceivedDate, setAccessReceivedDate] = useState("");
  const [allocationDate, setAllocationDate] = useState("");
  const [revisedStartDate, setRevisedStartDate] = useState("");
  const [revisedEndDate, setRevisedEndDate] = useState("");
  const [delegates, setDelegates] = useState([]);
  const [showNatureDropdown, setShowNatureDropdown] = useState(false);
  const initialValues = useRef({});

  useEffect(() => {
    if (show) {
      const noe = data.natureOfEngagement || "";
      const ard = formatToISO(data.accessReceivedDate);
      const ad = formatToISO(data.allocationDate);
      const rsd = formatToISO(data.revisedStartDate);
      const red = formatToISO(data.revisedEndDate);
      const delegateList = (data.engagementDelegates || [])
        .filter((d) => d.delegateEmail || d.emailAddress)
        .map((d) => {
          const nameParts = (d.delegateName || "").trim().split(/\s+/);
          return {
            emailAddress: d.delegateEmail || d.emailAddress,
            firstName: d.firstName || nameParts[0] || "",
            lastName: d.lastName || nameParts.slice(1).join(" ") || "",
          };
        });

      setNatureOfEngagement(noe);
      setAccessReceivedDate(ard);
      setAllocationDate(ad);
      setRevisedStartDate(rsd);
      setRevisedEndDate(red);
      setDelegates(delegateList);

      initialValues.current = {
        natureOfEngagement: noe,
        accessReceivedDate: ard,
        allocationDate: ad,
        revisedStartDate: rsd,
        revisedEndDate: red,
        delegatesKey: delegatesKey(delegateList),
      };
    }
  }, [
    show,
    data.natureOfEngagement,
    data.accessReceivedDate,
    data.allocationDate,
    data.revisedStartDate,
    data.revisedEndDate,
    data.engagementDelegates,
  ]);

  const currentDelegatesKey = delegatesKey(delegates);

  const dirty = {
    natureOfEngagement:
      natureOfEngagement !== initialValues.current.natureOfEngagement,
    accessReceivedDate:
      accessReceivedDate !== initialValues.current.accessReceivedDate,
    allocationDate: allocationDate !== initialValues.current.allocationDate,
    revisedStartDate:
      revisedStartDate !== initialValues.current.revisedStartDate,
    revisedEndDate: revisedEndDate !== initialValues.current.revisedEndDate,
    delegates: currentDelegatesKey !== initialValues.current.delegatesKey,
  };

  const hasChanges = Object.values(dirty).some(Boolean);

  const canEditRevisedStart = currentStageId == null || currentStageId <= 5;
  const canEditRevisedEnd = currentStageId == null || currentStageId <= 6;
  // Delegates acting on behalf of the EM must not edit the delegates list.
  const canEditDelegates =
    !isCurrentUserDelegate && (currentStageId == null || currentStageId <= 8);

  // Validate Revised Start Date <= Revised End Date at render time so the
  // Save button stays disabled and the error shows as soon as the user makes
  // the combo invalid (matches the ADN modal behavior).
  const shouldValidateRevisedDateRange =
    dirty.revisedStartDate || dirty.revisedEndDate;
  const hasInvalidRevisedDateRange =
    shouldValidateRevisedDateRange &&
    Boolean(revisedStartDate) &&
    Boolean(revisedEndDate) &&
    revisedStartDate > revisedEndDate;
  const revisedDateValidationError = hasInvalidRevisedDateRange
    ? "Revised Start Date must be less than or equal to Revised End Date."
    : "";

  // Enforce strict chronological ordering across the milestone dates whenever
  // the user has touched any of the involved fields:
  //   Allocation Date < Access Received Date
  // Equal dates are NOT allowed — each milestone must strictly precede the
  // next. (ISO yyyy-mm-dd strings sort correctly with a plain string compare.)
  const allocVsAccessInvalid =
    (dirty.allocationDate || dirty.accessReceivedDate) &&
    Boolean(allocationDate) &&
    Boolean(accessReceivedDate) &&
    allocationDate >= accessReceivedDate;
  const hasInvalidDateOrder = allocVsAccessInvalid;
  let dateOrderValidationError = "";
  if (allocVsAccessInvalid) {
    dateOrderValidationError =
      "Allocation Date must be before Access Received Date.";
  }

  const hasTooManyDelegates = delegates.length > MAX_DELEGATES;
  const delegatesValidationError = hasTooManyDelegates
    ? `You can add a maximum of ${MAX_DELEGATES} Engagement Delegates.`
    : "";

  const handleSave = () => {
    if (
      hasInvalidRevisedDateRange ||
      hasInvalidDateOrder ||
      hasTooManyDelegates
    )
      return;

    // Build a changes object containing ONLY dirty fields.
    // The parent will translate these into updateX payload entries;
    // omitted keys mean "do not touch this field".
    const changes = {};
    if (dirty.natureOfEngagement)
      changes.natureOfEngagement = natureOfEngagement;
    if (dirty.accessReceivedDate)
      changes.accessReceivedDate = accessReceivedDate;
    if (dirty.allocationDate) changes.allocationDate = allocationDate;
    if (dirty.revisedStartDate) changes.revisedStartDate = revisedStartDate;
    if (dirty.revisedEndDate) changes.revisedEndDate = revisedEndDate;
    if (dirty.delegates) changes.delegates = delegates;
    onSave(changes);
  };

  const body = (
    <div className="aoa-edit-service-details-body">
      <div className="aoa-edit-service-details-row">
        <div className="aoa-edit-service-details-field">
          <label className="field-label motif-body2-default-medium">
            Nature of Engagement
          </label>
          <MotifFormField>
            <MotifDropdown
              style={{ display: "block", width: "100%" }}
              open={showNatureDropdown}
              onClose={() => setShowNatureDropdown()}
              trigger={
                <button
                  type="button"
                  className="nature-dropdown-trigger"
                  onClick={() => setShowNatureDropdown(!showNatureDropdown)}
                >
                  <span>{natureOfEngagement || "Select..."}</span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="currentColor"
                  >
                    <path
                      d="M2.5 4.5L6 8L9.5 4.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      fill="none"
                    />
                  </svg>
                </button>
              }
            >
              {NATURE_OF_ENGAGEMENT_OPTIONS.map((option) => (
                <MotifDropdownItem
                  key={option}
                  onClick={() => {
                    setNatureOfEngagement(option);
                    setShowNatureDropdown(false);
                  }}
                >
                  {option}
                </MotifDropdownItem>
              ))}
            </MotifDropdown>
          </MotifFormField>
        </div>

        <div className="aoa-edit-service-details-field">
          <label className="field-label motif-body2-default-medium">
            Allocation Date
          </label>
          <MotifDatePicker
            className={`edit-modal-date${!allocationDate ? " motif-date-empty" : ""}`}
            format="dd/MM/y"
            value={allocationDate || ""}
            onChange={(val) => setAllocationDate(normalizeDateValue(val))}
          />
        </div>
      </div>

      <div className="aoa-edit-service-details-row">
        <div className="aoa-edit-service-details-field">
          <label className="field-label motif-body2-default-medium">
            Access Received Date
          </label>
          <MotifDatePicker
            className={`edit-modal-date${!accessReceivedDate ? " motif-date-empty" : ""}`}
            format="dd/MM/y"
            value={accessReceivedDate || ""}
            onChange={(val) => setAccessReceivedDate(normalizeDateValue(val))}
          />
        </div>

        <div className="aoa-edit-service-details-field">
          <label className="field-label motif-body2-default-medium">
            Revised Start Date
          </label>
          <MotifDatePicker
            className={`edit-modal-date${!revisedStartDate ? " motif-date-empty" : ""}`}
            format="dd/MM/y"
            value={revisedStartDate || ""}
            disabled={!canEditRevisedStart}
            onChange={(val) => {
              onClearError?.();
              setRevisedStartDate(normalizeDateValue(val));
            }}
          />
        </div>
      </div>

      <div className="aoa-edit-service-details-row">
        <div className="aoa-edit-service-details-field">
          <label className="field-label motif-body2-default-medium">
            Revised End Date
          </label>
          <MotifDatePicker
            className={`edit-modal-date${!revisedEndDate ? " motif-date-empty" : ""}`}
            format="dd/MM/y"
            value={revisedEndDate || ""}
            disabled={!canEditRevisedEnd}
            onChange={(val) => {
              onClearError?.();
              setRevisedEndDate(normalizeDateValue(val));
            }}
          />
        </div>

        <div className="aoa-edit-service-details-field aoa-edit-service-details-field--delegates">
          <label className="field-label motif-body2-default-medium">
            Engagement Delegates
            <span
              className="aoa-delegates-info"
              tabIndex={0}
              aria-label="Ensure that you assign requests to employees who are either Seniors or higher ups."
            >
              <svg
                className="aoa-delegates-info-icon"
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  fillRule="evenodd"
                  d="M8 1.333a6.667 6.667 0 1 0 0 13.334A6.667 6.667 0 0 0 8 1.333ZM2.667 8a5.333 5.333 0 1 1 10.666 0A5.333 5.333 0 0 1 2.667 8Zm5.333-.667a.667.667 0 0 1 .667.667v2.667a.667.667 0 0 1-1.334 0V8A.667.667 0 0 1 8 7.333Zm0-2a.667.667 0 1 0 0 1.334.667.667 0 0 0 0-1.334Z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="aoa-delegates-tooltip" role="tooltip">
                Ensure that you assign requests to employees who are either
                Seniors or higher ups.
              </span>
            </span>
          </label>
          <EditableDelegateField
            delegates={delegates.map((d) => ({
              delegateEmail: d.emailAddress,
              firstName: d.firstName,
              lastName: d.lastName,
            }))}
            onChange={(value) => {
              onClearError?.();
              setDelegates(value);
            }}
            disabled={!canEditDelegates}
            maxItems={MAX_DELEGATES}
            limitReachedMessage={`You can add a maximum of ${MAX_DELEGATES} Engagement Delegates.`}
            menuPlacement="top"
          />
        </div>
      </div>

      {(dateOrderValidationError ||
        revisedDateValidationError ||
        delegatesValidationError ||
        error) && (
        <div className="aoa-edit-service-details-error" role="alert">
          {dateOrderValidationError ||
            revisedDateValidationError ||
            delegatesValidationError ||
            error}
        </div>
      )}
    </div>
  );

  const footer = (
    <div className="aoa-edit-service-details-footer">
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={handleSave}
        disabled={
          loading ||
          !hasChanges ||
          hasInvalidRevisedDateRange ||
          hasInvalidDateOrder ||
          hasTooManyDelegates
        }
      >
        {loading ? "Saving..." : "Save"}
      </Button>
    </div>
  );

  return (
    <Modal
      show={show}
      onClose={onClose}
      header="Edit Service Details"
      body={body}
      footer={footer}
      size="xl"
      className="aoa-edit-modal"
    />
  );
}
